import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useQrScanner } from '../../hooks/useQrScanner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Battery, Plus, Search, Clock, CheckCircle, AlertTriangle, XCircle, QrCode, History, ChevronDown, ChevronUp, RefreshCw, Download, TrendingUp, CalendarDays, ChevronLeft, ChevronRight, Bell, Send, Trash2, Upload, FileSpreadsheet } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const STATUS_CONFIG = {
  la_zi: { label: 'La zi', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200', dot: 'bg-emerald-500', icon: CheckCircle },
  scadent: { label: 'Scadent', color: 'bg-amber-500/10 text-amber-600 border-amber-200', dot: 'bg-amber-500', icon: Clock },
  intarziat: { label: 'Intarziat', color: 'bg-red-500/10 text-red-600 border-red-200', dot: 'bg-red-500', icon: AlertTriangle },
  fara_service: { label: 'Fara service', color: 'bg-slate-500/10 text-slate-500 border-slate-200', dot: 'bg-slate-400', icon: XCircle },
};

export default function BatteryServicePage() {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [stats, setStats] = useState({ total: 0, la_zi: 0, scadent: 0, intarziat: 0, fara_service: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [showHistory, setShowHistory] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sortField, setSortField] = useState('status');
  const [sortDir, setSortDir] = useState('asc');
  const [viewMode, setViewMode] = useState('table');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  // Service form
  const [serviceForm, setServiceForm] = useState({
    ore_functionare: '', data_completare: new Date().toISOString().slice(0, 10),
    cantitate_lichid: '', observatii: '', qr_validated: false, qr_scanned_code: ''
  });
  const [showQRScanner, setShowQRScanner] = useState(false);
  const { start: startQr, stop: stopQr } = useQrScanner();
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [alertSettings, setAlertSettings] = useState({ enabled: false, send_email: true, send_sms: false, alert_days: [14, 7, 3, 1], min_interval_hours: 24, recipients: [] });
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [eqRes, stRes, aRes] = await Promise.all([
        api.get('/battery-service/equipment'),
        api.get('/battery-service/stats'),
        api.get('/battery-service/alert-settings').catch(() => ({ data: { enabled: false, recipients: [] } }))
      ]);
      setEquipment(eqRes.data);
      setStats(stRes.data);
      setAlertSettings(aRes.data);
    } catch (e) {
      toast.error('Eroare la incarcarea datelor');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const departments = [...new Set(equipment.map(e => e.departament).filter(Boolean))].sort();

  const statusOrder = { intarziat: 0, fara_service: 1, scadent: 2, la_zi: 3 };
  const filtered = equipment
    .filter(e => {
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (filterDept !== 'all' && e.departament !== filterDept) return false;
      if (search) {
        const s = search.toLowerCase();
        return (e.denumire || '').toLowerCase().includes(s)
          || (e.marca || '').toLowerCase().includes(s)
          || (e.serie_utilaj || '').toLowerCase().includes(s)
          || (e.code || '').toLowerCase().includes(s)
          || (e.departament || '').toLowerCase().includes(s);
      }
      return true;
    })
    .sort((a, b) => {
      if (sortField === 'status') {
        const diff = (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
        return sortDir === 'asc' ? diff : -diff;
      }
      const va = a[sortField] || '';
      const vb = b[sortField] || '';
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });

  const openServiceDialog = (eq) => {
    setSelectedEquipment(eq);
    setServiceForm({
      ore_functionare: eq.last_ore_functionare || '',
      data_completare: new Date().toISOString().slice(0, 10),
      cantitate_lichid: '', observatii: '', qr_validated: false, qr_scanned_code: ''
    });
    setShowServiceDialog(true);
  };

  const handleSubmitService = async () => {
    if (!serviceForm.ore_functionare) { toast.error('Indexul de ore este obligatoriu'); return; }
    if (!serviceForm.qr_validated) { toast.error('Scanati codul QR al echipamentului pentru validare'); return; }
    try {
      await api.post('/battery-service', {
        equipment_id: selectedEquipment.id,
        ore_functionare: parseFloat(serviceForm.ore_functionare),
        data_completare: serviceForm.data_completare,
        cantitate_lichid: serviceForm.cantitate_lichid,
        observatii: serviceForm.observatii,
        qr_validated: serviceForm.qr_validated,
        qr_scanned_code: serviceForm.qr_scanned_code
      });
      toast.success('Interventie inregistrata cu succes!');
      setShowServiceDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const startQRScanner = async () => {
    setShowQRScanner(true);
    try {
      await new Promise(r => setTimeout(r, 300));
      await startQr('qr-reader-battery', handleQrScan, { fps: 10, qrbox: { width: 200, height: 200 } });
    } catch (e) {
      toast.error('Nu s-a putut porni camera');
      setShowQRScanner(false);
    }
  };

  const handleQrScan = async (decodedText) => {
    await stopQRScanner();
    // Extrage codul din formatele acceptate:
    //  - "ISCIR-000011" simplu
    //  - "ISCIR:ISCIR-000011" prefixat
    //  - URL QR public: "http(s)://domeniu/e/ISCIR-000011" sau "/equipment/ISCIR-000011"
    let scannedCode = (decodedText || '').trim();
    // URL -> ia ultimul segment al path-ului (ignora query/hash)
    const urlMatch = scannedCode.match(/^https?:\/\/[^\s]+$/i);
    if (urlMatch) {
      try {
        const u = new URL(scannedCode);
        const parts = u.pathname.split('/').filter(Boolean);
        if (parts.length > 0) scannedCode = parts[parts.length - 1];
      } catch { /* fallback sub */ }
    }
    // Prefix "ISCIR:"
    if (scannedCode.startsWith('ISCIR:')) scannedCode = scannedCode.slice(6).trim();
    const expectedCode = selectedEquipment?.code || '';
    if (scannedCode.toUpperCase() === expectedCode.toUpperCase()) {
      setServiceForm(f => ({ ...f, qr_validated: true, qr_scanned_code: scannedCode }));
      toast.success('QR validat cu succes!');
    } else {
      toast.error(`Cod QR incorect! Asteptat: ${expectedCode}, Scanat: ${scannedCode}`);
      setServiceForm(f => ({ ...f, qr_validated: false, qr_scanned_code: scannedCode }));
    }
  };

  const stopQRScanner = async () => {
    await stopQr();
    setShowQRScanner(false);
  };

  const loadHistory = async (eq) => {
    if (showHistory?.id === eq.id) { setShowHistory(null); return; }
    setShowHistory(eq);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/battery-service/history/${eq.id}`);
      setHistoryData(res.data);
    } catch { toast.error('Eroare la incarcarea istoricului'); }
    finally { setHistoryLoading(false); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortIcon = ({ field }) => sortField === field ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null;

  const handleExport = async () => {
    try {
      const res = await api.get(`/battery-service/export/xlsx?month=${exportMonth}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `service_baterii_${exportMonth}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Raport exportat!');
    } catch { toast.error('Eroare la export'); }
  };

  const handleSaveAlertSettings = async () => {
    try {
      await api.post('/battery-service/alert-settings', alertSettings);
      toast.success('Setari alerte service baterii salvate!');
      setShowAlertSettings(false);
    } catch { toast.error('Eroare la salvarea setarilor'); }
  };

  const addAlertRecipient = () => {
    setAlertSettings({
      ...alertSettings,
      recipients: [...alertSettings.recipients, { name: '', email: '', phone: '', notify_email: true, notify_sms: false }]
    });
  };

  const updateAlertRecipient = (idx, field, val) => {
    const r = [...alertSettings.recipients];
    r[idx] = { ...r[idx], [field]: val };
    setAlertSettings({ ...alertSettings, recipients: r });
  };

  const removeAlertRecipient = (idx) => {
    setAlertSettings({ ...alertSettings, recipients: alertSettings.recipients.filter((_, i) => i !== idx) });
  };

  const handleTestAlert = async () => {
    try {
      const res = await api.post('/battery-service/test-alert');
      toast.success(`Verificare executata: ${res.data.result?.sent || 0} alerte`);
    } catch { toast.error('Eroare la testare'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/battery-service/import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'sablon_import_service_baterii.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Eroare la descarcare sablon'); }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Selectati un fisier Excel'); return; }
    setImportLoading(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.post('/battery-service/import/xlsx', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`${res.data.imported} interventii importate cu succes!`);
        fetchData();
      }
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} erori la import`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la import');
    } finally { setImportLoading(false); }
  };

  // Prepare chart data from history (reversed to chronological order)
  const chartData = historyData.length > 1
    ? [...historyData].reverse().map(h => ({
        data: h.data_completare ? new Date(h.data_completare).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' }) : '',
        ore: h.ore_functionare || 0,
      }))
    : [];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-cyan-600" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="page-title">
            <Battery className="w-7 h-7 text-cyan-600" /> Service Baterii
          </h1>
          <p className="text-sm text-slate-500 mt-1">Evidenta completare lichid baterie - Lize electrice & Stivuitoare</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9 text-sm" onClick={() => setShowAlertSettings(true)} data-testid="battery-alert-settings-btn">
            <Bell className="w-4 h-4 mr-1.5" /> Alerte
            {alertSettings.enabled && <span className="ml-1 w-2 h-2 bg-emerald-500 rounded-full inline-block" />}
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-sm" onClick={() => { setShowImportDialog(true); setImportFile(null); setImportResult(null); }} data-testid="btn-import">
            <Upload className="w-4 h-4 mr-1.5" /> Import
          </Button>
          <Input type="month" value={exportMonth} onChange={e => setExportMonth(e.target.value)} className="h-9 w-40 text-sm" data-testid="export-month" />
          <Button variant="outline" size="sm" className="h-9 text-sm" onClick={handleExport} data-testid="btn-export">
            <Download className="w-4 h-4 mr-1.5" /> Export Raport
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: 'total', label: 'Total Echipamente', val: stats.total, cls: 'bg-slate-50 border-slate-200 text-slate-700' },
          { key: 'la_zi', label: 'La Zi', val: stats.la_zi, cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
          { key: 'scadent', label: 'Scadent', val: stats.scadent, cls: 'bg-amber-50 border-amber-200 text-amber-700' },
          { key: 'intarziat', label: 'Intarziat', val: stats.intarziat, cls: 'bg-red-50 border-red-200 text-red-700' },
          { key: 'fara_service', label: 'Fara Service', val: stats.fara_service, cls: 'bg-slate-50 border-slate-200 text-slate-500' },
        ].map(s => (
          <button key={s.key} onClick={() => setFilterStatus(filterStatus === s.key ? 'all' : s.key)}
            className={`p-3 rounded-lg border text-left transition-all ${s.cls} ${filterStatus === s.key ? 'ring-2 ring-cyan-400' : ''}`}
            data-testid={`stat-${s.key}`}>
            <div className="text-2xl font-bold">{s.val}</div>
            <div className="text-xs font-medium mt-0.5">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Filters + View Toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cauta dupa denumire, serie, departament..." className="pl-9 h-9" data-testid="search-input" />
        </div>
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="h-9 px-3 rounded-md border text-sm" data-testid="filter-dept">
          <option value="all">Toate departamentele</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {(filterStatus !== 'all' || filterDept !== 'all' || search) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterStatus('all'); setFilterDept('all'); setSearch(''); }} className="text-xs text-slate-400">Reseteaza filtre</Button>
        )}
        <div className="ml-auto flex items-center border rounded-lg overflow-hidden">
          <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'table' ? 'bg-cyan-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`} data-testid="view-table">Tabel</button>
          <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'calendar' ? 'bg-cyan-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`} data-testid="view-calendar">Calendar</button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (() => {
        const MONTHS = ['Ianuarie','Februarie','Martie','Aprilie','Mai','Iunie','Iulie','August','Septembrie','Octombrie','Noiembrie','Decembrie'];
        const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
        const firstDay = (new Date(calYear, calMonth, 1).getDay() + 6) % 7; // Monday = 0
        const today = new Date();
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);

        // Build map: day -> list of equipment scheduled/done
        const dayMap = {};
        filtered.forEach(eq => {
          const interval = eq.interval_service_zile || 30;
          if (eq.last_service_date) {
            const ld = new Date(eq.last_service_date);
            if (ld.getMonth() === calMonth && ld.getFullYear() === calYear) {
              const d = ld.getDate();
              if (!dayMap[d]) dayMap[d] = [];
              dayMap[d].push({ ...eq, type: 'done' });
            }
            // Next due date
            const next = new Date(ld);
            next.setDate(next.getDate() + interval);
            if (next.getMonth() === calMonth && next.getFullYear() === calYear) {
              const d = next.getDate();
              if (!dayMap[d]) dayMap[d] = [];
              dayMap[d].push({ ...eq, type: 'due' });
            }
          }
        });

        return (
          <div className="bg-white rounded-xl border shadow-sm p-4" data-testid="calendar-view">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }} className="p-1.5 rounded hover:bg-slate-100"><ChevronLeft className="w-5 h-5" /></button>
              <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-cyan-600" /> {MONTHS[calMonth]} {calYear}</h3>
              <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }} className="p-1.5 rounded hover:bg-slate-100"><ChevronRight className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
              {['Lun','Mar','Mie','Joi','Vin','Sam','Dum'].map(d => (
                <div key={d} className="bg-slate-50 p-2 text-center text-xs font-semibold text-slate-500">{d}</div>
              ))}
              {cells.map((day, i) => {
                if (!day) return <div key={`e${i}`} className="bg-white p-2 min-h-[80px]" />;
                const items = dayMap[day] || [];
                const isToday = day === today.getDate() && calMonth === today.getMonth() && calYear === today.getFullYear();
                return (
                  <div key={day} className={`bg-white p-1.5 min-h-[80px] ${isToday ? 'ring-2 ring-cyan-400 ring-inset' : ''}`}>
                    <span className={`text-xs font-medium ${isToday ? 'text-cyan-600 font-bold' : 'text-slate-400'}`}>{day}</span>
                    <div className="mt-1 space-y-0.5">
                      {items.slice(0, 3).map((it, j) => (
                        <div key={j} className={`text-[9px] px-1 py-0.5 rounded truncate cursor-pointer ${it.type === 'done' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                          title={`${it.denumire} - ${it.type === 'done' ? 'Efectuat' : 'Scadent'}`}
                          onClick={() => openServiceDialog(it)}>
                          {it.type === 'done' ? '✓' : '⏰'} {it.cod_alocare_intern || it.code?.slice(-3)}
                        </div>
                      ))}
                      {items.length > 3 && <div className="text-[9px] text-slate-400 text-center">+{items.length - 3}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-400" /> Efectuat</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-400" /> Scadent/Planificat</span>
            </div>
          </div>
        );
      })()}

      {/* Equipment Table */}
      {viewMode === 'table' && (
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="battery-table">
            <thead>
              <tr className="bg-slate-50 border-b">
                {[
                  { key: 'cod_alocare_intern', label: 'Nr.' },
                  { key: 'denumire', label: 'Echipament' },
                  { key: 'marca', label: 'Marca' },
                  { key: 'serie_utilaj', label: 'Serie' },
                  { key: 'departament', label: 'Departament' },
                  { key: 'last_ore_functionare', label: 'Index Ore' },
                  { key: 'last_service_date', label: 'Ultima Completare' },
                  { key: 'days_since_service', label: 'Zile' },
                  { key: 'status', label: 'Status' },
                ].map(col => (
                  <th key={col.key} className="p-3 text-left text-xs font-semibold text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleSort(col.key)}>
                    <span className="flex items-center gap-1">{col.label} <SortIcon field={col.key} /></span>
                  </th>
                ))}
                <th className="p-3 text-right text-xs font-semibold text-slate-500 uppercase">Actiuni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} className="p-8 text-center text-slate-400">
                  {equipment.length === 0 ? 'Niciun echipament configurat. Activati "Necesita completare lichid baterie" in Nomenclatorul ISCIR.' : 'Niciun rezultat pentru filtrele selectate.'}
                </td></tr>
              ) : filtered.map(eq => {
                const sc = STATUS_CONFIG[eq.status] || STATUS_CONFIG.fara_service;
                const StatusIcon = sc.icon;
                const isExpanded = showHistory?.id === eq.id;
                return (
                  <React.Fragment key={eq.id}>
                    <tr className={`border-b hover:bg-slate-50/50 transition-colors ${eq.status === 'intarziat' ? 'bg-red-50/30' : ''}`} data-testid={`row-${eq.code}`}>
                      <td className="p-3 font-mono text-xs text-slate-500">{eq.cod_alocare_intern || '-'}</td>
                      <td className="p-3">
                        <div className="font-medium text-slate-800">{eq.denumire}</div>
                        <div className="text-xs text-slate-400">{eq.code}</div>
                      </td>
                      <td className="p-3 text-slate-600">{eq.marca || '-'}</td>
                      <td className="p-3 font-mono text-xs text-slate-500">{eq.serie_utilaj || '-'}</td>
                      <td className="p-3 text-slate-600 text-xs">{eq.departament || '-'}</td>
                      <td className="p-3 font-mono font-medium text-slate-700">{eq.last_ore_functionare != null ? `${eq.last_ore_functionare} h` : '-'}</td>
                      <td className="p-3 text-xs text-slate-600">{eq.last_service_date ? new Date(eq.last_service_date).toLocaleDateString('ro-RO') : '-'}</td>
                      <td className="p-3 text-xs font-medium">{eq.days_since_service != null ? `${eq.days_since_service}z` : '-'}</td>
                      <td className="p-3">
                        <Badge className={`${sc.color} border text-xs gap-1`} data-testid={`status-${eq.code}`}>
                          <StatusIcon className="w-3 h-3" /> {sc.label}
                        </Badge>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 hover:text-slate-700" onClick={() => loadHistory(eq)} data-testid={`btn-history-${eq.code}`}>
                            <History className="w-3.5 h-3.5 mr-1" /> Istoric
                          </Button>
                          <Button size="sm" className="h-7 text-xs bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => openServiceDialog(eq)} data-testid={`btn-service-${eq.code}`}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Completare
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr><td colSpan={10} className="p-0 bg-slate-50/80">
                        <div className="p-4">
                          <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2"><History className="w-4 h-4" /> Istoric completari - {eq.denumire}</h4>
                          {historyLoading ? <p className="text-sm text-slate-400">Se incarca...</p> :
                            historyData.length === 0 ? <p className="text-sm text-slate-400">Nicio interventie inregistrata</p> : (
                            <div className="space-y-4">
                              {/* Hours Evolution Chart */}
                              {chartData.length > 1 && (
                                <div className="bg-white rounded-lg border p-4">
                                  <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-1.5">
                                    <TrendingUp className="w-3.5 h-3.5 text-cyan-600" /> Evolutie Ore Functionare
                                  </h5>
                                  <ResponsiveContainer width="100%" height={180}>
                                    <LineChart data={chartData}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                      <XAxis dataKey="data" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                                      <Tooltip
                                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                                        formatter={(v) => [`${v} h`, 'Ore functionare']}
                                      />
                                      <Line type="monotone" dataKey="ore" stroke="#0891b2" strokeWidth={2.5} dot={{ fill: '#0891b2', r: 4 }} activeDot={{ r: 6 }} />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              )}
                              {/* History Table */}
                              <table className="w-full text-xs">
                                <thead><tr className="bg-white border-b">
                                  <th className="p-2 text-left text-slate-500">Data</th>
                                  <th className="p-2 text-left text-slate-500">Index Ore</th>
                                  <th className="p-2 text-left text-slate-500">Cantitate</th>
                                  <th className="p-2 text-left text-slate-500">Efectuat de</th>
                                  <th className="p-2 text-left text-slate-500">QR Validat</th>
                                  <th className="p-2 text-left text-slate-500">Observatii</th>
                                </tr></thead>
                                <tbody>
                                  {historyData.map(h => (
                                    <tr key={h.id} className="border-b last:border-0">
                                      <td className="p-2 font-medium">{h.data_completare ? new Date(h.data_completare).toLocaleDateString('ro-RO') : '-'}</td>
                                      <td className="p-2 font-mono">{h.ore_functionare} h</td>
                                      <td className="p-2">{h.cantitate_lichid || '-'}</td>
                                      <td className="p-2">{h.performed_by || '-'}</td>
                                      <td className="p-2">{h.qr_validated ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}</td>
                                      <td className="p-2 text-slate-500 max-w-[200px] truncate">{h.observatii || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-cyan-600" /> Import Interventii din Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 border space-y-2">
              <p className="text-sm text-slate-600">Importati interventii de completare lichid baterie dintr-un fisier Excel (.xlsx).</p>
              <p className="text-xs text-slate-500">Coloanele necesare: <strong>Cod ISCIR</strong>, <strong>Ore Functionare</strong>, <strong>Data Completare</strong>, Cantitate Lichid, Observatii, QR Validat</p>
              <Button variant="link" size="sm" className="text-xs p-0 h-auto text-cyan-600" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                <Download className="w-3 h-3 mr-1" /> Descarca sablon Excel
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Selectati fisierul Excel</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={e => setImportFile(e.target.files?.[0] || null)}
                className="h-auto py-2"
                data-testid="import-file-input"
              />
            </div>

            {/* Import Result */}
            {importResult && (
              <div className="space-y-2 border rounded-lg p-3" data-testid="import-result">
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-emerald-600 font-medium">
                    <CheckCircle className="w-4 h-4" /> {importResult.imported} importate
                  </span>
                  {importResult.errors?.length > 0 && (
                    <span className="flex items-center gap-1 text-red-500 font-medium">
                      <XCircle className="w-4 h-4" /> {importResult.errors.length} erori
                    </span>
                  )}
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto bg-red-50 rounded p-2 space-y-1">
                    {importResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-600">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Inchide</Button>
            <Button
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              onClick={handleImport}
              disabled={!importFile || importLoading}
              data-testid="btn-import-submit"
            >
              {importLoading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              {importLoading ? 'Se importa...' : 'Importa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Settings Dialog */}
      <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="battery-alert-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5" /> Setari Alerte Service Baterii</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg">
              <div>
                <p className="font-medium">Alerte automate active</p>
                <p className="text-sm text-slate-500">Verificare zilnica a scadentelor service baterii</p>
              </div>
              <button
                onClick={() => setAlertSettings({ ...alertSettings, enabled: !alertSettings.enabled })}
                className={`w-12 h-7 rounded-full transition-colors relative ${alertSettings.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                data-testid="battery-alert-toggle"
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${alertSettings.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Channels */}
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={alertSettings.send_email} onChange={e => setAlertSettings({ ...alertSettings, send_email: e.target.checked })} className="w-4 h-4 rounded" data-testid="battery-alert-email-check" />
                <div><p className="font-medium text-sm">Email</p><p className="text-xs text-slate-500">Trimite alerte pe email</p></div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={alertSettings.send_sms} onChange={e => setAlertSettings({ ...alertSettings, send_sms: e.target.checked })} className="w-4 h-4 rounded" data-testid="battery-alert-sms-check" />
                <div><p className="font-medium text-sm">SMS</p><p className="text-xs text-slate-500">Trimite alerte prin SMS</p></div>
              </label>
            </div>

            {/* Alert Days */}
            <div className="space-y-2">
              <Label>Intervale alerta (zile inainte de scadenta service)</Label>
              <div className="flex gap-2 flex-wrap">
                {[30, 14, 7, 5, 3, 1].map(d => (
                  <button key={d}
                    onClick={() => {
                      const days = alertSettings.alert_days || [];
                      setAlertSettings({ ...alertSettings, alert_days: days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort((a, b) => b - a) });
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${(alertSettings.alert_days || []).includes(d) ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-500'}`}
                    data-testid={`battery-alert-day-${d}`}
                  >{d} zile</button>
                ))}
              </div>
            </div>

            {/* Min interval between alerts */}
            <div className="space-y-2">
              <Label>Interval minim intre alerte</Label>
              <p className="text-xs text-slate-500">Previne trimiterea multipla a alertelor la intervale scurte (ex: la repornirea serverului)</p>
              <div className="flex gap-2 flex-wrap">
                {[6, 12, 24, 48].map(h => (
                  <button key={h}
                    onClick={() => setAlertSettings({ ...alertSettings, min_interval_hours: h })}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${(alertSettings.min_interval_hours || 24) === h ? 'bg-emerald-100 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500'}`}
                    data-testid={`battery-interval-${h}h-btn`}
                  >{h} ore</button>
                ))}
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Destinatari alerte</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAlertRecipient} data-testid="battery-add-recipient-btn">
                  <Plus className="w-3 h-3 mr-1" /> Adauga
                </Button>
              </div>
              {(alertSettings.recipients || []).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Niciun destinatar configurat. Adaugati cel putin un destinatar.</p>
              )}
              {(alertSettings.recipients || []).map((r, idx) => (
                <div key={idx} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600">Destinatar {idx + 1}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeAlertRecipient(idx)} className="text-red-500 h-7" data-testid={`battery-remove-recipient-${idx}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Nume" value={r.name} onChange={e => updateAlertRecipient(idx, 'name', e.target.value)} className="h-9 text-sm" data-testid={`battery-recipient-name-${idx}`} />
                    <Input placeholder="Email" type="email" value={r.email} onChange={e => updateAlertRecipient(idx, 'email', e.target.value)} className="h-9 text-sm" data-testid={`battery-recipient-email-${idx}`} />
                    <Input placeholder="Telefon" value={r.phone} onChange={e => updateAlertRecipient(idx, 'phone', e.target.value)} className="h-9 text-sm" data-testid={`battery-recipient-phone-${idx}`} />
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={r.notify_email} onChange={e => updateAlertRecipient(idx, 'notify_email', e.target.checked)} className="w-3.5 h-3.5 rounded" /> Email
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={r.notify_sms} onChange={e => updateAlertRecipient(idx, 'notify_sms', e.target.checked)} className="w-3.5 h-3.5 rounded" /> SMS
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={handleTestAlert} data-testid="battery-test-alert-btn">
              <Send className="w-4 h-4 mr-2" /> Testeaza Acum
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAlertSettings(false)}>Anuleaza</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleSaveAlertSettings} data-testid="battery-save-alert-settings-btn">Salveaza Setari</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={v => { if (!v) { stopQRScanner(); setShowServiceDialog(false); } }}>
        <DialogContent className="max-w-lg" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Battery className="w-5 h-5 text-cyan-600" />
              Inregistrare Completare Lichid
            </DialogTitle>
          </DialogHeader>

          {selectedEquipment && (
            <div className="space-y-4">
              {/* Equipment Info */}
              <div className="bg-slate-50 rounded-lg p-3 border">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-800">{selectedEquipment.denumire}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {selectedEquipment.marca} | {selectedEquipment.serie_utilaj} | {selectedEquipment.departament}
                    </div>
                  </div>
                  <Badge variant="outline" className="font-mono text-xs">{selectedEquipment.code}</Badge>
                </div>
              </div>

              {/* Form */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Index Ore Functionare *</Label>
                  <Input type="number" step="0.1" value={serviceForm.ore_functionare} onChange={e => setServiceForm(f => ({ ...f, ore_functionare: e.target.value }))}
                    placeholder="Ex: 3450.5" className="h-10 font-mono" data-testid="input-ore" />
                  {selectedEquipment.last_ore_functionare != null && (
                    <p className="text-xs text-slate-400">Ultimul index: {selectedEquipment.last_ore_functionare} h</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Data Completare</Label>
                  <Input type="date" value={serviceForm.data_completare} onChange={e => setServiceForm(f => ({ ...f, data_completare: e.target.value }))}
                    className="h-10" data-testid="input-data" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Cantitate Lichid Adaugat</Label>
                <Input value={serviceForm.cantitate_lichid} onChange={e => setServiceForm(f => ({ ...f, cantitate_lichid: e.target.value }))}
                  placeholder="Ex: 2 litri, Nivel completat" className="h-10" data-testid="input-cantitate" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Observatii</Label>
                <Textarea value={serviceForm.observatii} onChange={e => setServiceForm(f => ({ ...f, observatii: e.target.value }))}
                  placeholder="Observatii suplimentare..." rows={2} data-testid="input-observatii" />
              </div>

              {/* QR Validation */}
              <div className="border-t pt-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Validare QR</h3>
                {serviceForm.qr_validated ? (
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="qr-validated">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">QR Validat cu succes</p>
                      <p className="text-xs text-emerald-600">Cod: {serviceForm.qr_scanned_code}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="ml-auto text-xs" onClick={() => setServiceForm(f => ({ ...f, qr_validated: false, qr_scanned_code: '' }))}>Rescaneaza</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {showQRScanner ? (
                      <div>
                        <div id="qr-reader-battery" className="w-full rounded-lg overflow-hidden" />
                        <Button variant="outline" size="sm" className="mt-2 w-full" onClick={stopQRScanner}>Anuleaza scanare</Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3 p-4 border-2 border-dashed border-slate-200 rounded-lg">
                        <QrCode className="w-8 h-8 text-slate-300" />
                        <p className="text-xs text-slate-400 text-center">Scanati codul QR de pe echipament ({selectedEquipment.code}) pentru a valida interventia</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="text-xs" onClick={startQRScanner} data-testid="btn-scan-qr">
                            <QrCode className="w-3.5 h-3.5 mr-1" /> Scaneaza QR
                          </Button>
                          <Button size="sm" variant="ghost" className="text-xs text-slate-400" onClick={() => {
                            setServiceForm(f => ({ ...f, qr_validated: true, qr_scanned_code: selectedEquipment.code }));
                            toast.success('Validare manuala acceptata');
                          }} data-testid="btn-manual-validate">
                            Validare manuala
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => { stopQRScanner(); setShowServiceDialog(false); }}>Anuleaza</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleSubmitService} disabled={!serviceForm.qr_validated} data-testid="btn-save-service">
              <CheckCircle className="w-4 h-4 mr-1" /> Salveaza Interventie
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
